/**
 * Pure helpers for the Preview panel: the data a renderer would send (the manifest defaults plus the Data panel's
 * test values, image choices as package paths), the call log, and the custom actions offered.
 */
import { assetPackagePath, type Scene } from '@prism-graphics/scene-schema';
import type { CallRecord } from '@prism-graphics/harness/frame/client';

type Manifest = Record<string, unknown>;

/** Defaults from the manifest schema, overlaid with the Data panel's values (image choices → package paths). */
export function previewData(manifest: Manifest, scene: Pick<Scene, 'fields' | 'assets'> | undefined, testData: Record<string, unknown>): Record<string, unknown> {
  const props = ((manifest.schema as { properties?: Record<string, { default?: unknown }> } | undefined)?.properties ?? {}) as Record<string, { default?: unknown }>;
  const data: Record<string, unknown> = Object.fromEntries(Object.entries(props).map(([k, v]) => [k, v.default]));
  for (const f of scene?.fields ?? []) {
    if (!(f.id in props) || !Object.prototype.hasOwnProperty.call(testData, f.id)) continue;
    let v = testData[f.id];
    if (f.type === 'image-choice' && typeof v === 'string' && v.startsWith('sha256:')) {
      const a = scene!.assets.find((x) => x.hash === v);
      v = a ? assetPackagePath(a) : v;
    }
    data[f.id] = v;
  }
  return data;
}

/** A package's layouts (ADR 0037): one per renderRequirements entry, primary first, with v_layout's id and label. */
export interface PackageLayout {
  id: string;
  label: string;
  width: number;
  height: number;
}

export function layoutsOfManifest(manifest: Manifest): PackageLayout[] {
  const reqs = (manifest.renderRequirements as Array<{ resolution?: { width?: { ideal?: number }; height?: { ideal?: number } }; v_layout?: { id?: string; label?: string } }> | undefined) ?? [];
  return reqs.map((r, i) => {
    const width = r.resolution?.width?.ideal ?? 1920;
    const height = r.resolution?.height?.ideal ?? 1080;
    const id = r.v_layout?.id ?? String(i);
    return { id, label: r.v_layout?.label ?? id.replace('x', ':'), width, height };
  });
}

export interface CustomActionInfo {
  id: string;
  name: string;
  description?: string;
  schema?: Record<string, unknown>;
}

export function customActionsOf(manifest: Manifest): CustomActionInfo[] {
  return ((manifest.customActions as CustomActionInfo[] | undefined) ?? []).filter((a) => typeof a?.id === 'string');
}

/** A payload template from a custom action's JSON Schema (defaults where given). */
export function payloadTemplate(schema: Record<string, unknown> | undefined): unknown {
  if (!schema) return null;
  const props = (schema.properties as Record<string, { default?: unknown; type?: string }> | undefined) ?? {};
  if (schema.type === 'object' || Object.keys(props).length) {
    return Object.fromEntries(Object.entries(props).map(([k, p]) => [k, p.default ?? (p.type === 'number' || p.type === 'integer' ? 0 : p.type === 'boolean' ? false : '')]));
  }
  return (schema as { default?: unknown }).default ?? null;
}

export type LogEntry =
  | { kind: 'call'; key: number; record: CallRecord; repeat: number }
  | { kind: 'console'; key: number; text: string; level: 'error' | 'warn' }
  | { kind: 'info'; key: number; text: string };

export const LOG_LIMIT = 400;

/**
 * Add or update a call record in the log (newest last). Consecutive goToTime calls (scrubbing, timeline playback)
 * collapse into one entry that counts them.
 */
export function logRecord(log: LogEntry[], record: CallRecord): LogEntry[] {
  const i = log.findIndex((e) => e.kind === 'call' && e.record.id === record.id);
  if (i >= 0) {
    const e = log[i] as Extract<LogEntry, { kind: 'call' }>;
    const next = [...log];
    next[i] = { ...e, record };
    return next;
  }
  const last = log[log.length - 1];
  if (record.method === 'goToTime' && last?.kind === 'call' && last.record.method === 'goToTime' && last.record.settled) {
    return [...log.slice(0, -1), { ...last, record, repeat: last.repeat + 1 }];
  }
  return [...log, { kind: 'call' as const, key: record.id, record, repeat: 1 }].slice(-LOG_LIMIT);
}

/** JSON for the log, shortened. */
export function short(value: unknown, max = 160): string {
  let s: string;
  try {
    s = JSON.stringify(value) ?? 'undefined';
  } catch {
    s = String(value);
  }
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

export function statusOf(r: CallRecord): number | undefined {
  return (r.result as { statusCode?: number } | undefined)?.statusCode;
}

export function isFailure(r: CallRecord): boolean {
  const s = statusOf(r);
  return Boolean(r.error) || (s !== undefined && (s < 200 || s > 299));
}
